import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Plus, PhoneIncoming, PhoneOutgoing, MessageSquare, FileText, Mail, Users, CalendarClock } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const JOURNAL_TYPES = [
  { value: 'phone_in',      label: 'Anruf erhalten',     icon: 'PhoneIncoming'  },
  { value: 'phone_out',     label: 'Anruf getaetigt',    icon: 'PhoneOutgoing'  },
  { value: 'messenger_in',  label: 'Messenger erhalten', icon: 'MessageSquare'  },
  { value: 'messenger_out', label: 'Messenger gesendet', icon: 'MessageSquare'  },
  { value: 'email_in',      label: 'E-Mail erhalten',    icon: 'Mail'           },
  { value: 'email_out',     label: 'E-Mail gesendet',    icon: 'Mail'           },
  { value: 'personal',      label: 'Pers. Kontakt',      icon: 'Users'          },
  { value: 'note',          label: 'Interne Notiz',      icon: 'FileText'       },
];

const ICON_MAP = { PhoneIncoming, PhoneOutgoing, MessageSquare, Mail, Users, FileText };

function typeInfo(value) {
  return JOURNAL_TYPES.find(t => t.value === value) || { label: value, icon: 'FileText' };
}

function formatDate(iso) {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function JournalPage() {
  const [entries, setEntries]         = useState([]);
  const [customers, setCustomers]     = useState([]);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [loading, setLoading]         = useState(true);
  const [panelOpen, setPanelOpen]     = useState(false);
  const [panelEntry, setPanelEntry]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterCustomer ? `?customer_id=${filterCustomer}` : '';
      const res = await api.get('/journal' + params);
      setEntries(res.data || []);
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [filterCustomer]);

  useEffect(() => {
    api.get('/customers?limit=200').then(r => setCustomers(r.data.customers || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setPanelEntry(null); setPanelOpen(true); };
  const openView   = (e) => { setPanelEntry(e);   setPanelOpen(true); };
  const closePanel = () => { setPanelOpen(false); load(); };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <BookOpen size={20} className="text-primary-500" />
          <h1 className="text-xl font-semibold text-gray-900">Journal</h1>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={16} />
          Eintrag
        </button>
      </div>

      <div className="mb-4">
        <select
          value={filterCustomer}
          onChange={e => setFilterCustomer(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300 w-full sm:w-64"
        >
          <option value="">Alle Kunden</option>
          {customers.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Datum</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Kunde</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Typ</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Notiz</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Wiedervorlage</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">Laden...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">Keine Eintr\u00e4ge vorhanden</td></tr>
            ) : entries.map(e => {
              const t = typeInfo(e.type);
              const Icon = ICON_MAP[t.icon] || FileText;
              return (
                <tr
                  key={e.id}
                  onClick={() => openView(e)}
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(e.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{e.customer_name}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-gray-600">
                      <Icon size={14} className="text-primary-500 shrink-0" />
                      {t.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{e.note}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {e.followup_at ? (
                      <span className="flex items-center gap-1 text-amber-600">
                        <CalendarClock size={13} />
                        {formatDate(e.followup_at)}
                      </span>
                    ) : '\u2014'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        {loading ? (
          <p className="px-4 py-8 text-center text-gray-400 text-sm">Laden...</p>
        ) : entries.length === 0 ? (
          <p className="px-4 py-8 text-center text-gray-400 text-sm">Keine Einträge vorhanden</p>
        ) : entries.map(e => {
          const t = typeInfo(e.type);
          const Icon = ICON_MAP[t.icon] || FileText;
          return (
            <div
              key={e.id}
              onClick={() => openView(e)}
              className="px-4 py-3 active:bg-gray-50 cursor-pointer transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-gray-900 text-sm truncate">{e.customer_name}</span>
                <span className="text-xs text-gray-400 shrink-0 ml-2">{formatDate(e.created_at)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                <Icon size={13} className="text-primary-500 shrink-0" />
                {t.label}
              </div>
              <p className="text-sm text-gray-600 truncate">{e.note}</p>
              {e.followup_at && (
                <span className="inline-flex items-center gap-1 text-xs text-amber-600 mt-1">
                  <CalendarClock size={12} />
                  {formatDate(e.followup_at)}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {panelOpen && (
        <JournalPagePanel
          entry={panelEntry}
          customers={customers}
          onClose={closePanel}
        />
      )}
    </div>
  );
}

function JournalPagePanel({ entry, customers, onClose }) {
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(
    entry
      ? {
          customer_id: entry.customer_id,
          contact_id:  entry.contact_id || '',
          type:        entry.type,
          note:        entry.note,
          followup_at: entry.followup_at ? entry.followup_at.split('T')[0] : '',
        }
      : { customer_id: '', contact_id: '', type: 'note', note: '', followup_at: '' }
  );
  const [saving, setSaving] = useState(false);
  const isView = !!entry;

  useEffect(() => {
    if (!form.customer_id) { setContacts([]); return; }
    api.get('/contacts/customer/' + form.customer_id).then(r => setContacts(r.data || [])).catch(() => {});
  }, [form.customer_id]);

  const handleSave = async () => {
    if (!form.customer_id) { toast.error('Bitte Kunde auswaehlen'); return; }
    if (!form.note.trim())  { toast.error('Notiz erforderlich'); return; }
    setSaving(true);
    try {
      await api.post('/journal', {
        customer_id: parseInt(form.customer_id),
        contact_id:  form.contact_id ? parseInt(form.contact_id) : null,
        type:        form.type,
        note:        form.note,
        followup_at: form.followup_at || null,
      });
      toast.success('Eintrag gespeichert');
      onClose();
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md h-full flex flex-col shadow-2xl overflow-y-auto">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-primary-500" />
            <h2 className="font-semibold text-gray-900 text-sm">{isView ? 'Journal-Eintrag' : 'Neuer Eintrag'}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">\u2715</button>
        </div>
        <div className="p-4 flex flex-col gap-4 flex-1">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Kunde *</label>
            <select
              value={form.customer_id}
              onChange={e => setForm(f => ({ ...f, customer_id: e.target.value, contact_id: '' }))}
              disabled={isView}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:bg-gray-50"
            >
              <option value="">Bitte auswaehlen...</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Typ *</label>
            <div className="grid grid-cols-2 gap-2">
              {JOURNAL_TYPES.map(t => {
                const Icon = ICON_MAP[t.icon] || FileText;
                const active = form.type === t.value;
                return (
                  <button
                    key={t.value}
                    onClick={() => !isView && setForm(f => ({ ...f, type: t.value }))}
                    disabled={isView}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                      active ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    } disabled:cursor-default`}
                  >
                    <Icon size={14} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notiz *</label>
            <textarea
              value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              readOnly={isView}
              rows={5}
              placeholder="Was ist passiert?"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none read-only:bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Wiedervorlage</label>
            <input
              type="date"
              value={form.followup_at}
              onChange={e => setForm(f => ({ ...f, followup_at: e.target.value }))}
              readOnly={isView}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 read-only:bg-gray-50"
            />
          </div>
        </div>
        {!isView && (
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {saving ? 'Speichern...' : 'Eintrag speichern'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
